import React from 'react';
import { NAV_LINKS, ASSETS } from '../constants';
import { Facebook, Linkedin } from 'lucide-react';
import { useScrollTo } from '../hooks/useScrollTo';

const Footer: React.FC = () => {
  // Automatically gets the current year
  const currentYear = new Date().getFullYear();
  const scrollTo = useScrollTo();

  return (
    <footer className="bg-black pt-16 pb-8 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
             {/* Column 1: Identity */}
             <div>
                <a 
                    href="#hero" 
                    onClick={(e) => scrollTo(e, '#hero')}
                    className="block mb-4"
                >
                    <img 
                        src={ASSETS.LOGO}
                        alt="Christopher Nwosu Logo" 
                        className="h-16 w-auto object-contain" 
                    />
                </a>
                <p className="text-slate-500 text-sm leading-relaxed">
                    Electrical Engineer and Solid Control Specialist
                </p>
             </div>
             
             {/* Column 2: My Services */}
             <div>
                <h4 className="text-white font-bold uppercase tracking-wider mb-6 text-sm">My Services</h4>
                <ul className="space-y-3">
                    <li><a href="#services" onClick={(e) => scrollTo(e, '#services')} className="text-slate-500 hover:text-industrial-500 transition-colors text-sm">Electrical Engineering</a></li>
                    <li><a href="#services" onClick={(e) => scrollTo(e, '#services')} className="text-slate-500 hover:text-industrial-500 transition-colors text-sm">Solid Control Specialist</a></li>
                    <li><a href="#services" onClick={(e) => scrollTo(e, '#services')} className="text-slate-500 hover:text-industrial-500 transition-colors text-sm">Risk Management</a></li>
                    <li><a href="#services" onClick={(e) => scrollTo(e, '#services')} className="text-slate-500 hover:text-industrial-500 transition-colors text-sm">Electrical Maintenance</a></li>
                    <li><a href="#services" onClick={(e) => scrollTo(e, '#services')} className="text-slate-500 hover:text-industrial-500 transition-colors text-sm">Project Support</a></li>
                </ul>
             </div>

             {/* Column 3: Useful Resources */}
             <div>
                <h4 className="text-white font-bold uppercase tracking-wider mb-6 text-sm">Useful Resources</h4>
                <ul className="space-y-3">
                    {NAV_LINKS.map((link) => (
                        <li key={link.name}>
                            <a 
                                href={link.href} 
                                onClick={(e) => scrollTo(e, link.href)}
                                className="text-slate-500 hover:text-industrial-500 transition-colors text-sm"
                            >
                                {link.name}
                            </a>
                        </li>
                    ))}
                </ul>
             </div>

             {/* Column 4: Follow Me (Socials) */}
             <div>
                <h4 className="text-white font-bold uppercase tracking-wider mb-6 text-sm">Follow Me</h4>
                <div className="flex gap-4">
                    <a href="#hero" onClick={(e) => scrollTo(e, '#hero')} className="p-2 bg-white/5 rounded-full hover:bg-industrial-500 hover:text-dark-950 transition-all transform hover:-translate-y-1 text-slate-400" aria-label="Facebook"><Facebook size={18} /></a>
                    <a href="#hero" onClick={(e) => scrollTo(e, '#hero')} className="p-2 bg-white/5 rounded-full hover:bg-industrial-500 hover:text-dark-950 transition-all transform hover:-translate-y-1 text-slate-400" aria-label="LinkedIn"><Linkedin size={18} /></a>
                    
                    {/* X (Twitter) Icon */}
                    <a href="#hero" onClick={(e) => scrollTo(e, '#hero')} className="p-2 bg-white/5 rounded-full hover:bg-industrial-500 hover:text-dark-950 transition-all transform hover:-translate-y-1 text-slate-400" aria-label="X">
                        <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="0" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zl-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                    </a>

                    {/* WhatsApp Icon */}
                    <a href="#hero" onClick={(e) => scrollTo(e, '#hero')} className="p-2 bg-white/5 rounded-full hover:bg-industrial-500 hover:text-dark-950 transition-all transform hover:-translate-y-1 text-slate-400" aria-label="WhatsApp">
                        <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="0" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                    </a>
                 </div>
             </div>
        </div>

        <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-slate-600 text-sm">
                © {currentYear} Christopher Nwosu | All Rights Reserved. Designed with <span className="text-red-500">❤️</span> by <a href="https://wa.link/mum28e" target="_blank" rel="noopener noreferrer" className="text-industrial-500 hover:text-white transition-colors">Ahiafor Digital</a>
            </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;